import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT, useDateFmt } from '../lang.js';
import Modal from '../Modal.jsx';
import Icon from '../Icon.jsx';
import { connectRealtime } from '../realtime.js';
import PageSkeleton, { SkRows } from '../Skeleton.jsx';
import Spin from '../Spin.jsx';

// Renkler durumla eşleşir; durum SUNUCUDA türetilir, panel yeniden hesaplamaz.
// Etiket sözlükten gelir: `tbl.status.<durum>`.
const STATUS_COLOR = {
  AVAILABLE: 'var(--ok)',
  OCCUPIED: 'var(--accent)',
  AWAITING_PAYMENT: 'var(--warn)',
  RESERVED: 'var(--muted)',
  OUT_OF_SERVICE: 'var(--bad)',
};

export default function Tables() {
  const t = useT();
  const [data, setData] = useState(null);
  // Rol: popup'ta YALNIZ sunucunun kabul edeceği işi göstermek için. Bakiyeli
  // hesabı kapatmak zarar yazmaktır → müdür+ işi; garsona hiç düğme göstermeyip
  // 403'ü sonradan yüzüne vurmaktansa baştan kilitli ve gerekçeli durur.
  const [role, setRole] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null);
  const [newTable, setNewTable] = useState({ seats: 2 });
  const [newArea, setNewArea] = useState('');
  // Kapatılmakta olan masanın kimliği: popup kapanır, masanın KENDİSİ dönen
  // halkayla bekler. Kapatma sunucuda kilit alır; o bir saniyede ızgarada
  // hiçbir şey olmayınca garson ikinci kez basıyordu.
  const [closing, setClosing] = useState(null);
  // Masa/alan kurulumu artık sayfanın altında DEĞİL, sağ üstteki dişlide:
  // servis sırasında her gün bakılan şey masa ızgarası; kurulum bir kez yapılır.
  const [setup, setSetup] = useState(false);

  const load = () => api('/api/tables').then((d) => {
    setData(d);
    // Açık popup varsa taze kaydıyla değiştir: tahsilat sonrası eski tutarı
    // göstermesin (eskiden popup'taki rakam donuyordu).
    setSel((s) => (s ? d.tables.find((x) => x.id === s.id) || null : null));
  }).catch((e) => setErr(errMessage(t, e)));
  useEffect(() => {
    load();
    api('/api/restaurant').then((d) => setRole(d.me?.role || null)).catch(() => {});
    // Salon ekranı canlı: masa durumu değişince (sipariş, ödeme, misafir
    // hesabı istedi) kendiliğinden boyanır — garson F5'e basmasın.
    const sock = connectRealtime();
    sock?.on('tables:changed', load);
    return () => sock?.off('tables:changed', load);
    /* eslint-disable-next-line */
  }, []);

  const call = async (path, opts) => {
    setErr('');
    try { const r = await api(path, opts); await load(); return r; } catch (e) { setErr(errMessage(t, e)); }
  };

  /**
   * Masayı kapat. Popup HEMEN kapanır: kasiyer kararını verdi, bundan sonra
   * bakması gereken yer salon ızgarası — kapanan masa orada halka döndürür,
   * iş bitince kendiliğinden yeşile (AVAILABLE) düşer.
   */
  const closeSession = async (table, reason) => {
    setSel(null);
    setClosing(table.id);
    try {
      await call(`/api/sessions/${table.session.id}/close`, { method: 'POST', body: { reason } });
    } finally {
      setClosing(null);
    }
  };

  if (err && !data) return <p className="err">{err}</p>;
  if (!data) return <PageSkeleton shape="grid" />;

  const byArea = new Map();
  for (const tb of data.tables) {
    const key = tb.areaId || 0;
    if (!byArea.has(key)) byArea.set(key, []);
    byArea.get(key).push(tb);
  }
  const areaName = (id) => data.areas.find((a) => a.id === id)?.name || t('tbl.noArea');
  const limitText = data.limits.maxTables == null
    ? t('tbl.limitUnlimited', { used: data.limits.used })
    : t('tbl.limit', { used: data.limits.used, max: data.limits.maxTables });

  return (
    <>
      <div className="card">
        <div className="row">
          <h2 style={{ margin: 0 }}>{t('nav.tables')}</h2>
          <span className="muted">{limitText}</span>
          <span className="spacer" />
          <button className="btn-ico" onClick={() => setSetup(true)}
            aria-label={t('tbl.setup')} title={t('tbl.setup')}><Icon n="gear" /></button>
        </div>
      </div>

      {[...byArea.entries()].map(([areaId, tables]) => (
        <div className="card" key={areaId}>
          <h2>{areaId ? areaName(areaId) : t('tbl.noArea')}</h2>
          <div className="tablegrid">
            {tables.map((tb) => (
              <button key={tb.id} className={`tablecard st-${tb.status}${closing === tb.id ? ' busy' : ''}`}
                disabled={closing === tb.id} onClick={() => setSel(tb)}>
                <span className="tname">{tb.name}</span>
                <span className="tmeta">{t('tbl.seats', { n: tb.seats })}</span>
                <span className="tstatus">{t(`tbl.status.${tb.status}`)}</span>
                {/* Bekleyen garson çağrısı: uyarı kartı 20 saniyede kaybolur,
                    masanın beklediği kaybolmamalı. */}
                {tb.waiterCalledAt && <span className="tcall"><Icon n="bell" />{t('tbl.waiterWaiting')}</span>}
                {/* Kartta KISA etiket: "Masa kapatılıyor…" 132px'lik karta sığmıyor,
                    tam cümle halkanın aria etiketinde duruyor. */}
                {closing === tb.id && (
                  <span className="tspin"><Spin label={t('tbl.closing')} />{t('tbl.closingShort')}</span>
                )}
                {tb.session && tb.session.dueMinor > 0 && (
                  <span className="tdue">{money(tb.session.dueMinor, tb.session.currency)}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Renk anahtarı masaların ALTINDA: ızgaraya bakarken renkler zaten
          tanıdık, açıklama yukarıda yer kaplamasın — gerekince aşağıda. */}
      <div className="card">
        <div className="row" style={{ gap: 14 }}>
          {Object.entries(STATUS_COLOR).map(([k, color]) => (
            <span key={k} className="muted" style={{ fontSize: 12 }}>
              <span className="dot" style={{ background: color }} />
              {t(`tbl.status.${k}`)}
            </span>
          ))}
        </div>
      </div>

      {sel && (
        <TableSheet table={sel} tables={data.tables} role={role} call={call}
          onCloseTable={(reason) => closeSession(sel, reason)}
          onClose={() => setSel(null)} t={t} />
      )}

      {setup && (
        <Modal title={t('tbl.setup')} onClose={() => setSetup(false)} wide>
          <section className="setup-sec">
            <h3>{t('tbl.addTable')}</h3>
            <div className="row">
              <input style={{ maxWidth: 160 }} placeholder={t('tbl.namePlaceholder')}
                value={newTable.name || ''} onChange={(e) => setNewTable({ ...newTable, name: e.target.value })} />
              <input style={{ maxWidth: 90 }} type="number" min="1" value={newTable.seats}
                onChange={(e) => setNewTable({ ...newTable, seats: Number(e.target.value) })} />
              <select style={{ maxWidth: 180 }} value={newTable.areaId || ''}
                onChange={(e) => setNewTable({ ...newTable, areaId: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">{t('tbl.areaNone')}</option>
                {data.areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button className="primary" onClick={async () => {
                if (!newTable.name?.trim()) return setErr(t('tbl.nameRequired'));
                await call('/api/tables', { method: 'POST', body: newTable });
                setNewTable({ seats: 2 });
              }}><Icon n="plus" />{t('add')}</button>
            </div>
            <p className="muted" style={{ marginBottom: 0 }}>{t('tbl.eachOwnQr')}</p>
          </section>

          <section className="setup-sec">
            <h3>{t('tbl.areas')}</h3>
            <div className="row">
              {data.areas.map((a) => (
                <span key={a.id} className="badge">
                  {a.name}{a.floor ? ` · ${a.floor}` : ''}
                  <button className="btn-ico" style={{ marginLeft: 6 }} aria-label={t('remove')}
                    title={t('remove')}
                    onClick={() => confirm(t('tbl.confirmRemoveArea', { name: a.name }))
                      && call(`/api/tables/areas/${a.id}`, { method: 'DELETE' })}><Icon n="trash" /></button>
                </span>
              ))}
            </div>
            <div className="row" style={{ marginTop: 10 }}>
              <input style={{ maxWidth: 220 }} placeholder={t('tbl.areaPlaceholder')}
                value={newArea} onChange={(e) => setNewArea(e.target.value)} />
              <button onClick={async () => {
                if (!newArea.trim()) return;
                await call('/api/tables/areas', { method: 'POST', body: { name: newArea } });
                setNewArea('');
              }}><Icon n="plus" />{t('tbl.addArea')}</button>
            </div>
          </section>
          {/* Baskı da kurulum işi: günde bir değil, masa eklenince yapılır —
              salon ızgarasının üstünde sürekli durmasın. */}
          <section className="setup-sec">
            <h3>{t('tbl.printQr')}</h3>
            <div className="row">
              <Link to="/qr"><button><Icon n="qr" />{t('tbl.printQr')}</button></Link>
              <Link to="/qr?mode=menu"><button><Icon n="printer" />{t('menu.printMenu')}</button></Link>
            </div>
            <p className="muted" style={{ marginBottom: 0 }}>{t('tbl.printQrNote')}</p>
          </section>

          {/* Hata popup'ın İÇİNDE de görünür: perde arkasındaki satırı kimse okuyamaz. */}
          {err && <p className="err">{err}</p>}
        </Modal>
      )}

      {err && <p className="err">{err}</p>}
    </>
  );
}

/**
 * Masa popup'ı. Servis sırasında açılır → en sık yapılan iş en üstte ve en
 * büyük: sipariş al. Geri kalanı iki sütunlu düğme ızgarası; geçmiş
 * katlanmış durur (okunmak için değil, gerektiğinde bakmak için).
 */
function TableSheet({ table, tables, role, call, onCloseTable, onClose, t }) {
  const nav = useNavigate();
  const fmt = useDateFmt();
  const [history, setHistory] = useState(null);
  const [moving, setMoving] = useState(false);
  const s = table.session;
  const free = tables.filter((x) => x.id !== table.id && x.status === 'AVAILABLE');
  // Bakiye kalarak kapatma = zarar yazma (sessions.js: MANAGER+ ve gerekçe şart).
  const mayWriteOff = role === 'OWNER' || role === 'MANAGER';
  const closeLocked = !!s && s.dueMinor > 0 && !mayWriteOff;

  const sub = [
    t('tbl.seats', { n: table.seats }),
    s && `#${s.code}`,
    s && s.dueMinor > 0 && `${t('tbl.remaining')} ${money(s.dueMinor, s.currency)}`,
  ].filter(Boolean).join(' · ');

  // Durum başlıkta: popup'ın ilk bakışta söylemesi gereken şey bu.
  const title = (
    <span className="sheet-title">
      {table.name}
      <span className={`badge st-badge st-${table.status}`}>{t(`tbl.status.${table.status}`)}</span>
    </span>
  );

  return (
    <Modal title={title} subtitle={sub} onClose={onClose}>
      <div className="acts">
        <button className="act act-primary act-wide" onClick={() => nav(`/order/${table.id}`)}>
          <Icon n="cart" />
          <b>{t('tbl.takeOrder')}</b>
          <span>{t('tbl.takeOrderHint')}</span>
        </button>

        {table.waiterCalledAt && (
          <button className="act" onClick={() => call(`/api/tables/${table.id}/ack-waiter`, { method: 'POST' }).then(onClose)}>
            <Icon n="bell" />
            <b>{t('tbl.ackWaiter')}</b><span>{t('tbl.ackWaiterHint')}</span>
          </button>
        )}

        {s ? (
          <>
            <button className="act" onClick={() => nav(`/sessions/${s.id}`)}>
              <Icon n="receipt" />
              <b>{t('tbl.openBill')}</b><span>{money(s.dueMinor, s.currency)}</span>
            </button>
            {s.status === 'OPEN' && (
              <button className="act" onClick={() => call(`/api/sessions/${s.id}/request-payment`, { method: 'POST' })}>
                <Icon n="bell" />
                <b>{t('tbl.billRequested')}</b><span>{t('tbl.billRequestedHint')}</span>
              </button>
            )}
            <button className="act" disabled={closeLocked} onClick={() => {
              const reason = s.dueMinor > 0
                ? prompt(t('tbl.closeReasonPrompt', { amount: money(s.dueMinor, s.currency) }))
                : null;
              if (s.dueMinor > 0 && !reason) return;
              onCloseTable(reason);
            }}>
              <Icon n={closeLocked ? 'lock' : 'checkCircle'} />
              <b>{t('tbl.closeBill')}</b>
              <span>{closeLocked ? t('tbl.closeNeedManager') : t('tbl.closeBillHint')}</span>
            </button>
            <button className="act" disabled={free.length === 0} onClick={() => setMoving((m) => !m)}>
              <Icon n="move" />
              <b>{t('tbl.move')}</b><span>{free.length ? t('tbl.moveHint') : t('tbl.moveNoFree')}</span>
            </button>
          </>
        ) : (
          <>
            <button className="act" onClick={() => call(`/api/tables/${table.id}/sessions`, { method: 'POST' })}>
              <Icon n="playCircle" />
              <b>{t('tbl.openTable')}</b><span>{t('tbl.openTableHint')}</span>
            </button>
            <label className="act act-field">
              <Icon n="sliders" />
              <b>{t('tbl.manualStatus')}</b>
              <select value={table.manualStatus || ''}
                onChange={(e) => call(`/api/tables/${table.id}`, { method: 'PATCH', body: { manualStatus: e.target.value || null } })}>
                <option value="">{t('tbl.manualNormal')}</option>
                <option value="RESERVED">{t('tbl.manualReserved')}</option>
                <option value="OUT_OF_SERVICE">{t('tbl.manualOutOfService')}</option>
              </select>
            </label>
          </>
        )}
      </div>

      {moving && (
        <div className="movebar">
          <span className="muted">{t('tbl.moveTo')}</span>
          {free.map((x) => (
            <button key={x.id} onClick={async () => {
              await call(`/api/sessions/${s.id}/move`, { method: 'POST', body: { tableId: x.id } });
              onClose();
            }}><Icon n="table" />{x.name}</button>
          ))}
        </div>
      )}

      <details className="sheet-more" onToggle={(e) => {
        if (e.target.open && history === null) {
          api(`/api/tables/${table.id}/sessions`).then(setHistory).catch(() => setHistory([]));
        }
      }}>
        <summary>{t('tbl.history')}</summary>
        {history === null ? <SkRows n={3} cols={['34%', '22%', '18%']} /> : history.length === 0 ? (
          <p className="muted">{t('tbl.noHistory')}</p>
        ) : (
          <table>
            <thead><tr><th>{t('tbl.code')}</th><th>{t('tbl.openedAt')}</th><th>{t('total')}</th></tr></thead>
            <tbody>
              {history.slice(0, 10).map((h) => (
                <tr key={h.id}>
                  <td>#{h.code}</td>
                  <td className="muted">{fmt.dateTime(h.openedAt)}</td>
                  <td>{money(h.totalMinor, h.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>

      <details className="sheet-more">
        <summary>{t('tbl.tableSettings')}</summary>
        <div className="row" style={{ marginTop: 8 }}>
          <button onClick={() => confirm(t('tbl.confirmRotate', { name: table.name }))
            && call(`/api/tables/${table.id}/qr/rotate`, { method: 'POST' })}>
            <Icon n="refresh" />{t('tbl.rotateQr', { v: table.qrVersion })}
          </button>
          <Link to="/qr"><button><Icon n="qr" />{t('tbl.printQr')}</button></Link>
        </div>
      </details>
    </Modal>
  );
}
